import { sharedJointStateManager } from './SharedJointStateManager';

/**
 * Manages drag state to defer heavy operations during active interactions
 * This prevents garbage collection and ensures smooth 60fps during drag operations
 */
class DragStateManager {
  private static instance: DragStateManager;
  private isDragging = false;
  private suppressWallUpdates = false;
  private activeWallId: string | null = null;
  private deferredOperations: (() => void)[] = [];

  // Set of wall IDs that are allowed to update during drag (dragged wall + immediate connections)
  private allowedUpdateWallIds: Set<string> = new Set();

  // Flag to suppress ALL wall updates (for middle handle drags, only allowed walls bypass this)
  private suppressAllWallUpdates = false;

  static getInstance(): DragStateManager {
    if (!DragStateManager.instance) {
      DragStateManager.instance = new DragStateManager();
    }
    return DragStateManager.instance;
  }

  /**
   * Start drag operation - defer all heavy operations
   * @param options.suppressWallUpdates - If true, suppresses updates only for the active wall
   * @param options.suppressAllWallUpdates - If true, suppresses ALL wall updates except allowed walls
   * @param options.wallId - The wall being dragged
   * @param options.allowedWallIds - Set of wall IDs allowed to update during drag
   */
  startDrag(options?: {
    suppressWallUpdates?: boolean;
    suppressAllWallUpdates?: boolean;
    wallId?: string;
    allowedWallIds?: Set<string>;
  }): void {
    this.isDragging = true;
    this.suppressWallUpdates = !!options?.suppressWallUpdates;
    this.suppressAllWallUpdates = !!options?.suppressAllWallUpdates;
    this.activeWallId = options?.wallId ?? null;
    this.allowedUpdateWallIds = options?.allowedWallIds ?? new Set();

    // Always allow the active wall
    if (this.activeWallId) {
      this.allowedUpdateWallIds.add(this.activeWallId);
    }
  }

  /**
   * End drag operation - execute all deferred operations
   */
  endDrag(): void {
    this.isDragging = false;
    this.suppressWallUpdates = false;
    this.suppressAllWallUpdates = false;
    this.activeWallId = null;
    this.allowedUpdateWallIds.clear();

    // Notify SharedJointStateManager that drag operation ended
    sharedJointStateManager.endDragOperation();

    // Execute all deferred operations when idle
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        this.executeDeferredOperations();
      });
    } else {
      setTimeout(() => {
        this.executeDeferredOperations();
      }, 0);
    }
  }

  /**
   * Check if currently dragging
   */
  isCurrentlyDragging(): boolean {
    return this.isDragging;
  }

  /**
   * Check if suppressing all wall updates (middle handle drag mode)
   */
  isSuppressingAllWallUpdates(): boolean {
    return this.suppressAllWallUpdates;
  }

  /**
   * Check if a wall is allowed to update during drag
   */
  isWallAllowedToUpdate(wallId: string): boolean {
    if (!this.suppressAllWallUpdates) return true;
    return this.allowedUpdateWallIds.has(wallId);
  }

  /**
   * Get the active wall ID being dragged
   */
  getActiveWallId(): string | null {
    return this.activeWallId;
  }

  shouldSuppressWallUpdate(wallId?: string): boolean {
    // If suppressing all wall updates, check if this wall is in the allowed set
    if (this.suppressAllWallUpdates) {
      if (!wallId) return true; // If no wallId provided, suppress
      return !this.allowedUpdateWallIds.has(wallId);
    }

    // Original behavior: suppress only for the active wall
    if (!this.suppressWallUpdates) return false;
    if (!wallId) return this.suppressWallUpdates;
    return this.activeWallId === wallId;
  }

  /**
   * Defer an operation until drag ends
   */
  defer(operation: () => void): void {
    if (this.isDragging) {
      this.deferredOperations.push(operation);
    } else {
      // Execute immediately if not dragging
      operation();
    }
  }

  /**
   * Execute operation only if not dragging, otherwise defer it
   */
  executeOrDefer(operation: () => void): void {
    if (this.isDragging) {
      this.deferredOperations.push(operation);
    } else {
      operation();
    }
  }

  private executeDeferredOperations(): void {
    const operations = [...this.deferredOperations];
    this.deferredOperations = [];

    operations.forEach(operation => {
      try {
        operation();
      } catch (error) {
        console.error('Error executing deferred operation:', error);
      }
    });
  }

  /**
   * Clear all deferred operations (emergency cleanup)
   */
  clear(): void {
    this.isDragging = false;
    this.suppressWallUpdates = false;
    this.activeWallId = null;
    this.deferredOperations = [];
  }
}

export default DragStateManager.getInstance();
